import React, { useState } from "react";
import BackButton from "../BackButton/BackButton";

export default function AdminAccountState() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [error, setError] = useState("");
  const [resetError, setResetError] = useState("");

  const [result, setResult] = useState(null);
  const [tempPassword, setTempPassword] = useState("");

  const [stripeCustomers, setStripeCustomers] = useState([]);
  const [stripeCustomersLoading, setStripeCustomersLoading] = useState(false);
  const [stripeCustomersError, setStripeCustomersError] = useState("");
  const [stripeCustomersHasMore, setStripeCustomersHasMore] = useState(false);
  const [stripeCustomersCursor, setStripeCustomersCursor] = useState(null);
  const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/?$/, "/");

  async function loadStripeCustomers({ reset = false } = {}) {
    setStripeCustomersError("");

    const token = localStorage.getItem("jwt");
    if (!token) {
      setStripeCustomersError(
        "No JWT found in localStorage.jwt. Log in first.",
      );
      return;
    }

    setStripeCustomersLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "25");
      if (!reset && stripeCustomersCursor) {
        params.set("starting_after", stripeCustomersCursor);
      }

      const res = await fetch(
        `${API_BASE}admin/stripe-customers?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        const msg =
          data?.error?.message ||
          data?.error ||
          data?.message ||
          `Request failed (${res.status})`;
        throw new Error(`${res.status}: ${msg}`);
      }

      const nextCustomers = Array.isArray(data.customers) ? data.customers : [];
      setStripeCustomers((prev) =>
        reset ? nextCustomers : [...prev, ...nextCustomers],
      );
      setStripeCustomersHasMore(!!data.page?.has_more);
      setStripeCustomersCursor(data.page?.next_starting_after || null);
    } catch (e) {
      setStripeCustomersError(e?.message || "Failed to load Stripe customers");
    } finally {
      setStripeCustomersLoading(false);
    }
  }

  async function handleLookup(e) {
    e.preventDefault();
    setError("");
    setResult(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter an email.");
      return;
    }

    const token = localStorage.getItem("jwt");
    if (!token) {
      setError("No JWT found in localStorage.jwt. Log in first.");
      return;
    }

    setLoading(true);
    try {
      const url = `${API_BASE}admin/account-state?email=${encodeURIComponent(
        trimmed,
      )}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        const msg =
          data?.error?.message ||
          data?.error ||
          data?.message ||
          `Request failed (${res.status})`;

        setError(`${res.status}: ${msg}`);
        return;
      }

      setResult(data);
    } catch (err) {
      setError(err?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword() {
    setResetError("");
    setTempPassword("");

    const trimmed = email.trim();
    if (!trimmed) {
      setResetError("Enter an email first.");
      return;
    }

    const token = localStorage.getItem("jwt");
    if (!token) {
      setResetError("No JWT found in localStorage.jwt. Log in first.");
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE}users/admin/reset-user-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: trimmed.toLowerCase() }),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        const msg =
          data?.error?.message ||
          data?.error ||
          data?.message ||
          `Request failed (${res.status})`;
        setResetError(`${res.status}: ${msg}`);
        return;
      }

      // Support a few common response shapes
      const pw =
        data?.temporaryPassword ||
        data?.tempPassword ||
        data?.password ||
        data?.value;

      if (!pw) {
        setResetError(
          "Reset succeeded but no temp password was returned. Check backend response.",
        );
        return;
      }

      setTempPassword(pw);
    } catch (err) {
      setResetError(err?.message || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  }

  async function copyTempPassword() {
    try {
      await navigator.clipboard.writeText(tempPassword);
    } catch {
      // If clipboard fails (rare), user can still manually select/copy
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      <BackButton to="/dashboard/projects" style={{ marginBottom: 20 }} />
      <h1 style={{ marginBottom: 8 }}>Admin: Account State</h1>
      <p style={{ marginTop: 0, opacity: 0.8 }}>
        Looks up a user by email and shows Stripe + subscription state.
      </p>

      <form onSubmit={handleLookup} style={{ display: "flex", gap: 8 }}>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@email.com"
          style={{ flex: 1, padding: 10, color: "white" }}
        />
        <button disabled={loading} style={{ padding: "10px 14px" }}>
          {loading ? "Loading..." : "Lookup"}
        </button>
      </form>

      {/* Reset password panel */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.15)",
          background: "rgba(17, 24, 39, 0.35)",
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleResetPassword}
            disabled={resetLoading}
            style={{ padding: "10px 14px" }}
          >
            {resetLoading ? "Resetting..." : "Generate Temporary Password"}
          </button>

          {tempPassword ? (
            <button
              type="button"
              onClick={copyTempPassword}
              style={{ padding: "10px 14px" }}
            >
              Copy Password
            </button>
          ) : null}
        </div>

        {resetError ? (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              border: "1px solid #f0b4b4",
              background: "#fff5f5",
            }}
          >
            <strong>Error:</strong> {resetError}
          </div>
        ) : null}

        {tempPassword ? (
          <div
            style={{
              marginTop: 10,
              padding: 12,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Temporary password:
            </div>
            <code style={{ fontSize: 16, wordBreak: "break-all" }}>
              {tempPassword}
            </code>
            <div style={{ marginTop: 8, opacity: 0.8, fontSize: 12 }}>
              Share this with the user. They should change it immediately after
              logging in.
            </div>
          </div>
        ) : null}
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid #f0b4b4",
            background: "#fff5f5",
            color: "#c53030",
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 20 }}>
          {/* === STATUS HEADER === */}
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              marginBottom: 20,
              background:
                result.summary.subscriptionStatus === "active"
                  ? "#e6fffa"
                  : "#fff5f5",
              border: "2px solid",
              borderColor:
                result.summary.subscriptionStatus === "active"
                  ? "#38b2ac"
                  : "#f56565",
            }}
          >
            <h2 style={{ margin: 0, color: "#111827" }}>
              {result.account.email}
            </h2>

            <div style={{ marginTop: 10, fontSize: 18, color: "#555" }}>
              Plan: <strong>{result.account.subscriptionPlan || "none"}</strong>
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 20,
                fontWeight: "bold",
                color:
                  result.summary.subscriptionStatus === "active"
                    ? "#2c7a7b"
                    : "#c53030",
              }}
            >
              {result.summary.subscriptionStatus?.toUpperCase()}
            </div>
          </div>

          {/* === ONBOARDING STATUS === */}
          <div
            style={{
              marginTop: 16,
              padding: 16,
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(17, 24, 39, 0.35)",
            }}
          >
            <h2 style={{ marginTop: 0 }}>Onboarding</h2>

            <div style={{ display: "grid", gap: 8 }}>
              <div>
                <strong>Flow:</strong>{" "}
                {result?.onboarding?.activeFlow || "none"}
              </div>

              <div>
                <strong>Checklist:</strong>{" "}
                {result?.onboarding?.completedStepsCount || 0}/
                {result?.onboarding?.totalSteps || 0} complete
              </div>

              <div>
                <strong>First win:</strong>{" "}
                {result?.onboarding?.firstWinCompletedAt
                  ? `Completed ${new Date(
                      result.onboarding.firstWinCompletedAt,
                    ).toLocaleString()}`
                  : "Not completed"}
              </div>

              <div>
                <strong>Last event:</strong>{" "}
                {result?.onboarding?.lastEventName || "none"}
              </div>

              <div>
                <strong>Last onboarding activity:</strong>{" "}
                {result?.onboarding?.lastEventAt
                  ? new Date(result.onboarding.lastEventAt).toLocaleString()
                  : "none"}
              </div>

              <div>
                <strong>Completed steps:</strong>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    background: "rgba(0,0,0,0.25)",
                    padding: 10,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(
                    result?.onboarding?.completedStepIds || [],
                    null,
                    2,
                  )}
                </pre>
              </div>

              <div>
                <strong>Completed events:</strong>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    background: "rgba(0,0,0,0.25)",
                    padding: 10,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(
                    result?.onboarding?.completedEventNames || [],
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          </div>

          {/* === BIG COUNT CARDS === */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            {[
              { label: "Customers", value: result.counts.customers },
              { label: "Projects", value: result.counts.projects },
              { label: "Estimates", value: result.counts.estimates },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: 20,
                  borderRadius: 12,
                  background: "#111827",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 14, opacity: 0.7 }}>{item.label}</div>
                <div style={{ fontSize: 42, fontWeight: "bold" }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {/* === ACCOUNT DETAILS === */}
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              marginBottom: 20,
            }}
          >
            <h3>Account Info</h3>
            <div>
              <strong>ID:</strong> {result.account.id}
            </div>
            <div>
              <strong>Name:</strong> {result.account.fullName}
            </div>
            <div>
              <strong>Created:</strong> {result.account.createdAt}
            </div>
          </div>

          {/* === STRIPE DETAILS === */}
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}
          >
            <h3>Stripe</h3>
            <div>
              <strong>Stripe Customer:</strong>{" "}
              {result.account.stripeCustomerId || "—"}
            </div>
            <div>
              <strong>Stripe Subscription:</strong>{" "}
              {result.account.stripeSubscriptionId || "—"}
            </div>
            <div>
              <strong>Has Stripe Customer:</strong>{" "}
              {result.summary.hasStripeCustomer ? "✅ Yes" : "❌ No"}
            </div>
            <div>
              <strong>Has Subscription:</strong>{" "}
              {result.summary.hasSubscription ? "✅ Yes" : "❌ No"}
            </div>
            <div>
              <strong>Will cancel at period end:</strong>{" "}
              {result.summary.willCancelAtPeriodEnd ? "⚠️ YES" : "No"}
            </div>

            <div>
              <strong>Cancel date:</strong>{" "}
              {result.summary.cancelAt
                ? new Date(result.summary.cancelAt).toLocaleString()
                : "—"}
            </div>

            <div>
              <strong>Current period end:</strong>{" "}
              {result.summary.currentPeriodEnd
                ? new Date(result.summary.currentPeriodEnd).toLocaleString()
                : "—"}
            </div>

            <div>
              <strong>Stripe status:</strong>{" "}
              {result.summary.stripeStatus || "—"}
            </div>
          </div>
          <div
            style={{
              marginTop: 20,
              padding: 20,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <h3 style={{ margin: 0 }}>Stripe Customers</h3>

              <button
                type="button"
                onClick={() => loadStripeCustomers({ reset: true })}
                disabled={stripeCustomersLoading}
                style={{ padding: "8px 12px" }}
              >
                {stripeCustomersLoading
                  ? "Loading..."
                  : stripeCustomers.length
                    ? "Refresh"
                    : "Load"}
              </button>

              {stripeCustomers.length ? (
                <button
                  type="button"
                  onClick={() => {
                    setStripeCustomers([]);
                    setStripeCustomersCursor(null);
                    setStripeCustomersHasMore(false);
                    setStripeCustomersError("");
                  }}
                  style={{ padding: "8px 12px" }}
                >
                  Clear
                </button>
              ) : null}
            </div>

            {stripeCustomersError ? (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  border: "1px solid #f0b4b4",
                  background: "#fff5f5",
                }}
              >
                <strong>Error:</strong> {stripeCustomersError}
              </div>
            ) : null}

            {stripeCustomers.length ? (
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: 8 }}>Email</th>
                      <th style={{ textAlign: "left", padding: 8 }}>Name</th>
                      <th style={{ textAlign: "left", padding: 8 }}>
                        Customer ID
                      </th>
                      <th style={{ textAlign: "left", padding: 8 }}>Created</th>
                      <th style={{ textAlign: "left", padding: 8 }}>
                        Delinquent
                      </th>
                      <th style={{ textAlign: "left", padding: 8 }}>Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stripeCustomers.map((c) => (
                      <tr key={c.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                        <td style={{ padding: 8 }}>{c.email || "—"}</td>
                        <td style={{ padding: 8 }}>{c.name || "—"}</td>
                        <td style={{ padding: 8 }}>
                          <code>{c.id}</code>
                        </td>
                        <td style={{ padding: 8 }}>
                          {c.created
                            ? new Date(c.created).toLocaleString()
                            : "—"}
                        </td>
                        <td style={{ padding: 8 }}>
                          {c.delinquent ? "⚠️" : "No"}
                        </td>
                        <td style={{ padding: 8 }}>
                          {c.livemode ? "LIVE" : "TEST"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {stripeCustomersHasMore ? (
                  <button
                    type="button"
                    onClick={() => loadStripeCustomers({ reset: false })}
                    disabled={stripeCustomersLoading}
                    style={{ marginTop: 12, padding: "10px 14px" }}
                  >
                    {stripeCustomersLoading ? "Loading..." : "Load more"}
                  </button>
                ) : (
                  <div style={{ marginTop: 12, opacity: 0.8 }}>
                    No more results.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 10, opacity: 0.8 }}>
                Click <strong>Load</strong> to fetch the most recent Stripe
                customers.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
